import React, { useEffect, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router";
import { useOrganizationVendors, usePrimaryOrganizationId } from "../../../hooks";
import { useDpa, useDpaAnswers, useDpaPrompts } from "../../../hooks/useDataProtectionAssessment";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import {
  ProgressFooter,
  ProgressFooterActions,
  ProgressFooterSaveNotification,
} from "../../get-compliant/ProgressFooter";
import { RouteHelpers, Routes } from "../../../root/routes";
import { PageHeader } from "../../../components/layout/header/Header";
import { CheckCircle, ExpandMore, Visibility } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
} from "@mui/material";
import { useActionRequest } from "../../../../common/hooks/api";
import {
  AddDPAAnswerRequest,
  DPAAnswerDto,
  DPAAuthorType,
  DPAPromptDto,
  DataProtectionAssessmentDto,
  UpdateDataProtectionAssessmentRequest,
  dpaTypeToHumanReadable,
} from "../../../../common/service/server/dto/DataProtectionAssessmentDto";
import { Api } from "../../../../common/service/Api";
import "../../../../../sass/components/dpa-write.scss";
import { WYSIWYGEditorInput } from "../../../../common/components/input/WYSIWYGEditor";
import { Form } from "react-final-form";
import { DPAProgressStepper } from "./DPAProgressStepper";
import { CloseableDialogTitle } from "../../../components/dialog/CloseableDialogTitle";
import { DPAWhyAmISeeingThisCard } from "./DPATips";
import { Link } from "react-router-dom";
import { DPAPdfUploadDialog } from "../../../components/dialog/DPAPdfUploadDialog";
import { Callout, CalloutVariant } from "../../../components/Callout";
import { ApiRouteHelpers } from "../../../../common/root/apiRoutes";
import { truncateAtWordBoundary } from "../../../util/text";
import { useDataMap } from "../../../../common/hooks/api/useDataMap";
import { DataMapDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import { OrganizationDataRecipientDto } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { formatDpaDate } from "./DPAPageHistory";

type MatchProps = {
  dpaId: string;
};
type Props = RouteComponentProps<MatchProps> & {
  add: boolean;
};

const DPAPillButton = ({
  buttonText,
  className,
  onClick,
}: {
  buttonText: string;
  className?: string;
  onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}) => {
  return (
    <div className={`dpa-sample-text-pill ${className}`} onClick={onClick}>
      <Visibility sx={{ width: "15px", height: "15px", color: "#918CA1" }} />
      <a href="#">{buttonText}</a>
    </div>
  );
};

const DPAQuestionAnswerSampleTextModal = ({
  sampleHtml,
  title,
  open,
  onClose,
  large,
}: {
  sampleHtml: string;
  title: string;
  open: boolean;
  onClose: () => void;
  large?: boolean;
}) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth={large === true ? "lg" : "sm"}>
      <CloseableDialogTitle onClose={onClose}>
        <div className="color-primary-700 text-weight-bold text-t4">{title}</div>
      </CloseableDialogTitle>
      <DialogContent>
        <div
          className="background-color-neutral-100 p-mdlg"
          dangerouslySetInnerHTML={{ __html: sampleHtml }}
        ></div>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DPAQuestionAnswerCard = ({
  dpa,
  questionNum,
  prompt,
  currentAnswer,
  onSave,
  dataMap,
  orgVendors,
}: {
  dpa: DataProtectionAssessmentDto;
  questionNum: number;
  prompt: DPAPromptDto;
  currentAnswer: string;
  onSave: (promptId: string, answer: string) => void;
  dataMap: DataMapDto;
  orgVendors: OrganizationDataRecipientDto[];
}) => {
  let isCategoriesQuestion = prompt.title === "Categories of Personal Data to be Processed";

  const maxHelpTextLengthToDisplay = 280;

  const pics = getPicsListsFromDataMap(dataMap, orgVendors);

  let finalHelpText = prompt.helpText.replace("$DPA_PURPOSE", dpaTypeToHumanReadable(dpa.type));
  if (shouldShowSampleText(dpa, prompt) && isCategoriesQuestion) {
    finalHelpText =
      "Click the link below to view the categories of personal data your business handles. " +
      finalHelpText;
  }

  const [isFullHelpTextDisplayed, setIsFullHelpTextDisplayed] = useState(false);

  const [isSampleTextModalOpen, setIsSampleTextModalOpen] = useState(false);

  let sampleText =
    dpa.type === "TARGETED_ADVERTISING"
      ? prompt.sampleTextTargetedAdvertising
      : dpa.type === "DATA_SELLING_GOOGLE_ANALYTICS"
      ? prompt.sampleTextGoogleAnalytics
      : dpa.type === "SNI"
      ? prompt.sampleTextSni
      : null;
  if (sampleText) {
    sampleText = renderSampleText(sampleText, pics, dataMap);
  }

  return (
    <div className="dpa-question-card mt-xl">
      <div className="dpa-question-card__question-description">
        <div className="text-weight-bold text-t4 color-neutral-900">{questionNum}.</div>
        <div className="pl-sm">
          <div className="text-weight-bold text-t4 color-neutral-900">
            {renderQuestionTitle(prompt)}
          </div>
          <div className="text-weight-regular text-t3 color-neutral-600">
            {finalHelpText.length > maxHelpTextLengthToDisplay ? (
              <>
                {(isFullHelpTextDisplayed
                  ? finalHelpText
                  : truncateAtWordBoundary(finalHelpText, maxHelpTextLengthToDisplay)) + "\u00a0"}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsFullHelpTextDisplayed(!isFullHelpTextDisplayed);
                  }}
                >
                  {isFullHelpTextDisplayed ? "collapse" : "expand"}
                </a>
              </>
            ) : (
              finalHelpText
            )}
          </div>
        </div>
      </div>
      <div className="dpa-question-card__answer">
        <Form
          initialValues={{ answer: currentAnswer }}
          onSubmit={(values) => {
            onSave(prompt.id, values.answer || "");
          }}
          render={({ handleSubmit }) => (
            <form>
              <WYSIWYGEditorInput field={`answer`} height="200px" onBlur={handleSubmit} />
              {shouldShowSampleText(dpa, prompt) && (
                <DPAPillButton
                  buttonText={isCategoriesQuestion ? "Categories" : "Sample Text"}
                  className="mt-md"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsSampleTextModalOpen(true);
                  }}
                />
              )}
            </form>
          )}
        />
      </div>
      <DPAQuestionAnswerSampleTextModal
        title={
          isCategoriesQuestion
            ? `Categories of Personal Data`
            : `Sample Data Protection Assessment: ${prompt.title}`
        }
        sampleHtml={sampleText}
        open={isSampleTextModalOpen}
        onClose={() => setIsSampleTextModalOpen(false)}
      />
    </div>
  );
};

const DPAAuthorForm = ({
  dpa,
  dpaAnswers,
  dpaPrompts,
  addDpaAnswer,
  dataMap,
  orgVendors,
}: {
  dpa: DataProtectionAssessmentDto;
  dpaAnswers: DPAAnswerDto[];
  dpaPrompts: DPAPromptDto[];
  addDpaAnswer: (addDpaAnswerRequest: AddDPAAnswerRequest) => void;
  dataMap: DataMapDto;
  orgVendors: OrganizationDataRecipientDto[];
}) => {
  const [showFullSampleTextModal, setShowFullSampleTextModal] = useState(false);

  return (
    <>
      <p className="text-weight-medium text-t3 color-neutral-900">
        {dpa.copiedFromPreviousDpa != null
          ? `These answers are copied from your previous ${dpaTypeToHumanReadable(
              dpa.type,
            )} DPA completed on ${formatDpaDate(
              dpa.copiedFromPreviousDpa.completedAt,
            )}. Adjust the answers below to reflect any material changes since then.`
          : `Use the assessment template below to create your customized document.`}
      </p>
      {(dpa.type === "TARGETED_ADVERTISING" || dpa.type === "DATA_SELLING_GOOGLE_ANALYTICS") && (
        <DPAPillButton
          onClick={(e) => {
            setShowFullSampleTextModal(true);
            e.preventDefault();
          }}
          buttonText={`View Sample DPA: ${dpa.name}`}
        />
      )}
      <div>
        {dpaPrompts.map((prompt, index) => (
          <DPAQuestionAnswerCard
            key={index}
            dpa={dpa}
            questionNum={index + 1}
            prompt={prompt}
            dataMap={dataMap}
            orgVendors={orgVendors}
            currentAnswer={dpaAnswers?.find((a) => a.dpaPromptId === prompt.id)?.answer || ""}
            onSave={(promptId: string, answer: string) => {
              addDpaAnswer({
                promptId,
                answer,
              } as AddDPAAnswerRequest);
            }}
          />
        ))}
      </div>
      <DPAQuestionAnswerSampleTextModal
        title={`Data Protection Assessment: ${dpaTypeToHumanReadable(dpa.type)}`}
        sampleHtml={renderFullDpaSampleText(
          dpa,
          dpaPrompts,
          getPicsListsFromDataMap(dataMap, orgVendors),
          dataMap,
        )}
        open={showFullSampleTextModal}
        onClose={() => setShowFullSampleTextModal(false)}
        large={true}
      />
    </>
  );
};

const DPAUploadFileForm = ({
  orgId,
  dpa,
  refreshDpa,
  isAlreadySigned,
  setIsAlreadySigned,
}: {
  orgId: string;
  dpa: DataProtectionAssessmentDto;
  refreshDpa: () => void;
  isAlreadySigned: boolean;
  setIsAlreadySigned: (isAlreadySigned: boolean) => void;
}) => {
  const history = useHistory();
  const [isPdfUploadDialogOpen, setIsPdfUploadDialogOpen] = useState(false);
  const [showPdfSuccessCallout, setShowPdfSuccessCallout] = useState(false);

  return (
    <div className="mt-lg">
      {showPdfSuccessCallout && (
        <Callout variant={CalloutVariant.Green} padding="0" className="mt-xl">
          <div className="d-flex flex--align-center">
            <div>
              <CheckCircle />
            </div>
            <div className="ml-md color-green-900 text-weight-bold text-t2 pb-xxxs">
              Your document has been successfully uploaded.
            </div>
          </div>
        </Callout>
      )}
      <div className="mt-md">
        <FormControlLabel
          style={{ alignItems: "flex-start" }}
          control={
            <Checkbox
              checked={isAlreadySigned}
              onChange={(event) => setIsAlreadySigned(event.target.checked)}
            />
          }
          label={
            <div style={{ paddingTop: "9px" }}>
              <div>This DPA is already signed</div>
              <div
                className="text-t1 color-neutral-600 mt-xxxs"
                style={{ maxWidth: "680px", lineHeight: "20px" }}
              >
                After filling out your DPA, TrueVault will send e-signature requests to your listed
                signers. Check this if the PDF already has signatures to skip this step.
              </div>
            </div>
          }
        />
      </div>
      <div className="mt-md">
        <Button
          className="ml-md"
          variant="contained"
          color="default"
          onClick={() => setIsPdfUploadDialogOpen(true)}
        >
          Upload completed PDF
        </Button>
      </div>
      <DPAPdfUploadDialog
        organizationId={orgId}
        dpaId={dpa.id}
        open={isPdfUploadDialogOpen}
        onClose={() => {
          setIsPdfUploadDialogOpen(false);
        }}
        onDone={() => {
          if (isAlreadySigned) {
            history.push(Routes.documents.historyDPA, { dpaUploadCompleted: true });
          } else {
            refreshDpa();
            setShowPdfSuccessCallout(true);
          }
        }}
        uploadPdf={
          isAlreadySigned
            ? (file) => Api.dataProtectionAssessment.uploadPreSignedPdf(orgId, dpa.id, file)
            : undefined
        }
        title={isAlreadySigned ? "Upload Pre-Signed DPA" : "Upload PDF"}
        subtitle={
          isAlreadySigned
            ? "Upload a signed .PDF file to complete this assessment without requesting signatures."
            : "File type needs to be .PDF only"
        }
        uploadButtonText={isAlreadySigned ? "Upload and Complete" : "Upload"}
      />
      {dpa.customPdfFilename && (
        <p>
          <a
            target="_blank"
            rel="noreferrer"
            href={(isAlreadySigned
              ? ApiRouteHelpers.organization.documents.dataProtectionAssessments.signedPdf
              : ApiRouteHelpers.organization.documents.dataProtectionAssessments.unsignedPdf)(
              orgId,
              dpa.id,
            )}
          >
            <b>{dpa.customPdfFilename}</b>
          </a>
        </p>
      )}
    </div>
  );
};

const DPAExternalProfessionalForm = () => {
  const externalBookingUrl =
    "https://app.boldsign.com/document/sign-bulk-links/?documentId=c9b6cd35-a311-46ed-96d4-508edde4c7b5s_uF2Kt";

  return (
    <div className="mt-lg">
      <div className="text-t3 text-weight-medium">Need extra help?</div>
      <div className="color-neutral-800 text-t2">
        TrueVault offers professional services to assist you with completing your DPA
      </div>
      <Accordion className="mt-md">
        <AccordionSummary expandIcon={<ExpandMore />}>View pricing details</AccordionSummary>
        <AccordionDetails>
          <p>
            Our DPA services involve a one-hour call with a Privacy Pro ($150) to collect some
            information from you, and a document preparation fee of $150 per DPA, for a base total
            of $300. If you request any additional call time with our Privacy Pro, you will be
            billed at a rate of $75 per each 30-minute increment.
          </p>
          <div>
            <Button
              variant="contained"
              color="default"
              onClick={() => window.open(externalBookingUrl, "_blank")}
            >
              Schedule your 1-hour call
            </Button>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export const DPAWrite: React.FC<Props> = ({ match }) => {
  const history = useHistory();
  const dpaId = match.params.dpaId;
  const orgId = usePrimaryOrganizationId();

  const [dpa, dpaRequestState, refreshDpa] = useDpa(orgId, dpaId);
  const [dpaAnswers, dpaAnswersRequestState, refreshDpaAnswers] = useDpaAnswers(orgId, dpa?.id);
  const [dpaPrompts, dpaPromptsRequestState] = useDpaPrompts(orgId);

  const [selectedTab, setSelectedTab] = useState<DPAAuthorType | "EXTERNAL" | null>(null);
  const [isUploadedDpaAlreadySigned, setIsUploadedDpaAlreadySigned] = useState(false);

  const dataMap = useDataMap(orgId);
  const [orgVendors, , ,] = useOrganizationVendors(orgId);

  useEffect(() => {
    if (dpa && dpa.authorType) {
      setSelectedTab(dpa.authorType);
    }
  }, [dpa]);

  const { fetch: updateDpa, request: updateDpaRequestState } = useActionRequest({
    api: async (updateDpaRequest: UpdateDataProtectionAssessmentRequest) => {
      await Api.dataProtectionAssessment.updateDpa(orgId, dpaId, updateDpaRequest);
      await refreshDpa();

      if (updateDpaRequest.authorType) {
        setSelectedTab(updateDpaRequest.authorType);
      }
    },
  });

  const { fetch: addDpaAnswer, request: addDpaAnswerRequestState } = useActionRequest({
    api: async (addDpaAnswerRequest: AddDPAAnswerRequest) => {
      await Api.dataProtectionAssessment.addDpaAnswer(orgId, dpaId, addDpaAnswerRequest);
      await refreshDpaAnswers();
    },
  });

  const isDpaComplete = (
    dpa: DataProtectionAssessmentDto,
    dpaAnswers: DPAAnswerDto[],
    dpaPrompts: DPAPromptDto[],
  ): boolean => {
    if (!dpa || dpa.authorType === null) return false;

    if (dpa.authorType === "SELF_AUTHOR") {
      if (!dpaPrompts) return false;
      return dpaPrompts
        .filter((d) => d.isOptional === false)
        .every((prompt) => {
          let answer = dpaAnswers?.find((a) => a.dpaPromptId === prompt.id);
          return answer && answer.answer.length > 0;
        });
    } else if (dpa.authorType === "USER_UPLOAD") {
      return dpa.customPdfFilename && dpa.customPdfFilename.length > 0;
    }

    return false;
  };

  return (
    <StandardPageWrapper
      requests={[dpaRequestState, dpaAnswersRequestState, dpaPromptsRequestState]}
      footer={
        <ProgressFooter
          content={
            <ProgressFooterSaveNotification
              request={[addDpaAnswerRequestState, updateDpaRequestState]}
            />
          }
          actions={
            <ProgressFooterActions
              nextContent={"Next"}
              nextLoading={false}
              nextOnClick={() =>
                history.push(
                  RouteHelpers.documents.dataProtectionAssessment.addApprovalParties(dpaId),
                )
              }
              nextDisabled={
                updateDpaRequestState.running ||
                dpa?.authorType === null ||
                !isDpaComplete(dpa, dpaAnswers, dpaPrompts)
              }
              nextDisabledTooltip={
                dpa?.authorType === null
                  ? "Please select the type of DPA."
                  : dpa?.authorType === "USER_UPLOAD"
                  ? "Please upload the completed DPA .pdf file."
                  : "Please complete the document before proceeding to the next step."
              }
            />
          }
        />
      }
    >
      <Link to={Routes.documents.dataProtectionAssessments} className="text-decoration-none">
        <div
          className="text-t1 color-neutral-700"
          style={{ lineHeight: "21px", letterSpacing: "1.12px" }}
        >
          &lt; BACK TO MY ASSESSMENTS
        </div>
      </Link>
      <PageHeader titleContent={dpa?.name} />
      <div className="mt-mdlg mb-lg">
        <DPAProgressStepper
          activeStep={0}
          writingOnly={selectedTab === "USER_UPLOAD" && isUploadedDpaAlreadySigned}
        />
      </div>
      <div className="text-color-primary text-t5 text-weight-medium">
        Write your Data Protection Assessment
      </div>
      <DPAWhyAmISeeingThisCard orgId={orgId} dpaType={dpa?.type} dataMap={dataMap?.data} />

      <div className="mt-lg">
        <div className="text-t5 text-weight-bold color-neutral-900">
          How to start writing your DPA
        </div>
        <p className="text-t3 text-color-neutral-800">
          We have created a basic template with our document builder to help you get started. You
          could also contact a Privacy Pro who can help you create your personalized DPA for your
          business.
        </p>
        <div className="d-flex">
          <Button
            variant="contained"
            color={selectedTab === "SELF_AUTHOR" ? "primary" : "default"}
            onClick={() => updateDpa({ authorType: "SELF_AUTHOR" })}
            disabled={updateDpaRequestState.running}
          >
            Build your own doc
          </Button>
          <Button
            variant="contained"
            color={selectedTab === "EXTERNAL" ? "primary" : "default"}
            className="ml-md"
            onClick={() => setSelectedTab("EXTERNAL")}
            disabled={updateDpaRequestState.running}
          >
            Hire a professional
          </Button>
          <Button
            variant="contained"
            color={selectedTab === "USER_UPLOAD" ? "primary" : "default"}
            className="ml-md"
            onClick={() => updateDpa({ authorType: "USER_UPLOAD" })}
            disabled={updateDpaRequestState.running}
          >
            Upload DPA
          </Button>
        </div>
      </div>

      {selectedTab === "SELF_AUTHOR" && (
        <div>
          <DPAAuthorForm
            dpa={dpa}
            dpaAnswers={dpaAnswers}
            dpaPrompts={dpaPrompts}
            addDpaAnswer={addDpaAnswer}
            dataMap={dataMap?.data}
            orgVendors={orgVendors}
          />
        </div>
      )}
      {selectedTab === "EXTERNAL" && (
        <div>
          <DPAExternalProfessionalForm />
        </div>
      )}
      {selectedTab === "USER_UPLOAD" && (
        <div>
          <DPAUploadFileForm
            orgId={orgId}
            dpa={dpa}
            refreshDpa={refreshDpa}
            isAlreadySigned={isUploadedDpaAlreadySigned}
            setIsAlreadySigned={setIsUploadedDpaAlreadySigned}
          />
        </div>
      )}
    </StandardPageWrapper>
  );
};

const shouldShowSampleText = (dpa: DataProtectionAssessmentDto, dpaPrompt: DPAPromptDto) => {
  if (!dpa || !dpaPrompt) return false;

  if (dpa.type === "TARGETED_ADVERTISING")
    return (
      dpaPrompt.sampleTextTargetedAdvertising && dpaPrompt.sampleTextTargetedAdvertising.length > 0
    );

  if (dpa.type === "DATA_SELLING_GOOGLE_ANALYTICS")
    return dpaPrompt.sampleTextGoogleAnalytics && dpaPrompt.sampleTextGoogleAnalytics.length > 0;

  if (dpa.type === "SNI") return dpaPrompt.sampleTextSni && dpaPrompt.sampleTextSni.length > 0;

  return false;
};

const htmlStringListForPicIds = (picIds: string[], dataMap: DataMapDto): string => {
  const sortedPicIds = picIds.sort((aPicId, bPicId) => {
    const a = dataMap.categories[aPicId];
    const b = dataMap.categories[bPicId];
    if (a.picGroupOrder != b.picGroupOrder) return a.picGroupOrder - b.picGroupOrder;
    return a.order - b.order;
  });
  const listElems = sortedPicIds.map((picId) => {
    const pic = dataMap.categories[picId];
    return `<li>${pic.name}</li>`;
  });
  return `<ul>${listElems.join("")}</ul>`;
};

const getPicsListsFromDataMap = (
  dataMap: DataMapDto,
  orgVendors: OrganizationDataRecipientDto[],
) => {
  if (!dataMap || !orgVendors) {
    return {
      allUniquePicIds: [],
      picIdsDisclosedToSharingAdNetwork: [],
      picIdsDisclosedToSharingAdNetworkWithCustomAudience: [],
      picIdsDisclosedToSellingVendors: [],
    };
  }

  // Full list of PICs
  const allUniquePicIds = Array.from(new Set(dataMap.collection.map((c) => c.picId)));

  // PICs disclosed to a "sharing" ad network
  const adNetworkVendorIds = Object.values(dataMap.recipients)
    .filter((r) => r.category === "Ad Network")
    .map((r) => r.vendorId);
  const sharingAdNetworkVendorIds = adNetworkVendorIds.filter((vendorId) => {
    const orgVendor = orgVendors.find((ov) => ov.vendorId === vendorId);
    return orgVendor && orgVendor.ccpaIsSharing;
  });
  const picIdsDisclosedToSharingAdNetwork = Array.from(
    new Set(
      dataMap.disclosure
        .filter((d) => sharingAdNetworkVendorIds.includes(d.vendorId))
        .map((d) => d.picId),
    ),
  );

  // PICs disclosed to a "sharing" ad network with custom audience enabled
  const sharingAdNetworksWithCustomAudienceVendorIds = adNetworkVendorIds.filter((vendorId) => {
    const orgVendor = orgVendors.find((ov) => ov.vendorId === vendorId);
    return orgVendor && orgVendor.ccpaIsSharing && orgVendor.usesCustomAudience;
  });
  const picIdsDisclosedToSharingAdNetworkWithCustomAudience = Array.from(
    new Set(
      dataMap.disclosure
        .filter((d) => sharingAdNetworksWithCustomAudienceVendorIds.includes(d.vendorId))
        .map((d) => d.picId),
    ),
  );

  // PICs disclosed to selling vendors
  const allVendorIds = Object.values(dataMap.recipients).map((r) => r.vendorId);
  const sellingVendorIds = allVendorIds.filter((vendorId) => {
    const orgVendor = orgVendors.find((ov) => ov.vendorId === vendorId);
    return orgVendor && orgVendor.ccpaIsSelling;
  });
  const picIdsDisclosedToSellingVendors = Array.from(
    new Set(
      dataMap.disclosure.filter((d) => sellingVendorIds.includes(d.vendorId)).map((d) => d.picId),
    ),
  );

  // PICs disclosed to SNI (Shopify Network Intelligence)
  const sniVendorIds = allVendorIds.filter((vendorId) => {
    const orgVendor = orgVendors.find((ov) => ov.vendorId === vendorId);
    return orgVendor && orgVendor.vendorKey === "shopify-audiences";
  });
  const picIdsDisclosedToSNI = Array.from(
    new Set(
      dataMap.disclosure.filter((d) => sniVendorIds.includes(d.vendorId)).map((d) => d.picId),
    ),
  );

  return {
    allUniquePicIds,
    picIdsDisclosedToSharingAdNetwork,
    picIdsDisclosedToSharingAdNetworkWithCustomAudience,
    picIdsDisclosedToSellingVendors,
    picIdsDisclosedToSNI,
  };
};

const renderSampleText = (
  sampleText: string,
  pics: ReturnType<typeof getPicsListsFromDataMap>,
  dataMap: DataMapDto,
) => {
  let result = sampleText.replace(
    "$ALL_PICS",
    htmlStringListForPicIds(pics.allUniquePicIds, dataMap),
  );
  result = result.replace(
    "$INTEREST_ADVERTISING_PICS",
    htmlStringListForPicIds(pics.picIdsDisclosedToSharingAdNetwork, dataMap),
  );
  result = result.replace(
    "$CUSTOM_AUDIENCE_PICS",
    htmlStringListForPicIds(pics.picIdsDisclosedToSharingAdNetworkWithCustomAudience, dataMap),
  );
  result = result.replace(
    "$SELLING_PICS",
    htmlStringListForPicIds(pics.picIdsDisclosedToSellingVendors, dataMap),
  );
  result = result.replace("$SNI_PICS", htmlStringListForPicIds(pics.picIdsDisclosedToSNI, dataMap));
  return result;
};

const renderQuestionTitle = (prompt: DPAPromptDto) =>
  prompt.isOptional ? "(Optional) " + prompt.title : prompt.title;

const renderFullDpaSampleText = (
  dpa: DataProtectionAssessmentDto,
  prompts: DPAPromptDto[],
  pics: ReturnType<typeof getPicsListsFromDataMap>,
  dataMap: DataMapDto,
) => {
  if (dpa?.type != "TARGETED_ADVERTISING" && dpa?.type != "DATA_SELLING_GOOGLE_ANALYTICS")
    return "";

  let result = "";

  for (let prompt of prompts) {
    if (!shouldShowSampleText(dpa, prompt)) continue;

    let sampleText =
      dpa.type === "TARGETED_ADVERTISING"
        ? prompt.sampleTextTargetedAdvertising
        : prompt.sampleTextGoogleAnalytics;
    let title = renderQuestionTitle(prompt);
    let body = renderSampleText(sampleText, pics, dataMap);

    result += `<h4>${title}</h4><p>${body}</p>`;
  }

  return result;
};
